/**
 * Word import helpers (ER-12400 — Article Checkpoint import from Word).
 *
 * Mirrors `pdfImport.js`: client-side latency checks for `WordImportDialog` and the backend
 * error-code → user-facing message mapping. The service layer re-validates everything; the
 * client checks only avoid a wasted upload.
 */
import { codeFromPayload, formatPdfFileSize, toCount } from "@components/utils/pdfImport";

export const WORD_DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const WORD_IMPORT_MAX_BYTES = 25 * 1024 * 1024;

/** Backend error codes. */
export const WORD_IMPORT_ERROR_CODES = {
  INVALID_FILE: "WORD_IMPORT_INVALID_FILE",
  LEGACY_FORMAT: "WORD_IMPORT_LEGACY_FORMAT",
  ENCRYPTED: "WORD_IMPORT_ENCRYPTED",
  FILE_TOO_LARGE: "WORD_IMPORT_FILE_TOO_LARGE",
  NO_CONTENT: "WORD_IMPORT_NO_CONTENT",
};

/** Distinct user-facing copy for each backend error code. */
export const WORD_IMPORT_ERROR_MESSAGES = {
  [WORD_IMPORT_ERROR_CODES.INVALID_FILE]: "That file isn't a valid Word document.",
  [WORD_IMPORT_ERROR_CODES.LEGACY_FORMAT]:
    "Older .doc files aren't supported. Save the document as .docx and try again.",
  [WORD_IMPORT_ERROR_CODES.ENCRYPTED]:
    "This document is password-protected. Remove the password and try again.",
  [WORD_IMPORT_ERROR_CODES.FILE_TOO_LARGE]: "This document is larger than 25 MB.",
  [WORD_IMPORT_ERROR_CODES.NO_CONTENT]: "This document has no text or images to import.",
};

/** Fallback for an unknown code, a job failure or a timeout. */
export const DEFAULT_WORD_IMPORT_ERROR_MESSAGE =
  "We couldn't import this document. Try again, or contact support if it keeps failing.";

/** Picker-state copy when more than one file is dropped or chosen. */
export const WORD_IMPORT_MULTIPLE_FILES_MESSAGE = "Select one Word document at a time.";

/**
 * Client-side latency check: is this file a .docx within the size limit?
 *
 * @param {File} file the dropped / chosen file
 * @returns {string} the error code to show, or "" when the file is acceptable
 */
export function validateWordFile(file) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  if (type !== WORD_DOCX_MIME_TYPE && !name.endsWith(".docx")) {
    if (name.endsWith(".doc") || type === "application/msword") {
      return WORD_IMPORT_ERROR_CODES.LEGACY_FORMAT;
    }
    return WORD_IMPORT_ERROR_CODES.INVALID_FILE;
  }
  if (Number(file?.size) > WORD_IMPORT_MAX_BYTES) {
    return WORD_IMPORT_ERROR_CODES.FILE_TOO_LARGE;
  }
  return "";
}

/**
 * Resolve a backend error code to its user-facing message.
 *
 * @param {string} code backend error code
 * @param {string} [fallback] message used when the code is unknown
 * @returns {string}
 */
export function getWordImportErrorMessage(code, fallback = DEFAULT_WORD_IMPORT_ERROR_MESSAGE) {
  return WORD_IMPORT_ERROR_MESSAGES[code] || fallback;
}

/**
 * User-facing message for a rejected start request (422).
 *
 * @param {*} error axios error
 * @returns {string}
 */
export function getWordImportStartErrorMessage(error) {
  return getWordImportErrorMessage(codeFromPayload(error?.response?.data));
}

/**
 * User-facing message for a job that ended `failed`; the status payload carries the code when
 * it is a known one, otherwise the generic fallback.
 *
 * @param {object|null} data the failed status payload
 * @returns {string}
 */
export function getWordImportJobFailureMessage(data) {
  return getWordImportErrorMessage(codeFromPayload(data));
}

/** Human-readable size for the selected-file card, e.g. "2.4 MB". */
export const formatWordFileSize = formatPdfFileSize;

/**
 * Rule 8 import summary shown once the editor is populated.
 *
 * "Imported {block_count} blocks and {image_count} images." plus, only when something was
 * left out, " Not carried over: …" as a comma-separated list. Zero counts are omitted.
 *
 * @param {object} summary `result.summary` of a succeeded Word import
 * @returns {string} the toast text, or "" when the result carries no summary
 */
export function buildWordImportSummaryMessage(summary) {
  if (!summary || typeof summary !== "object") {
    return "";
  }
  const skipped = summary.skipped && typeof summary.skipped === "object" ? summary.skipped : {};
  const failedImages = Array.isArray(summary.failed_images)
    ? summary.failed_images.length
    : toCount(skipped.failed_images);
  const mergedCells = toCount(skipped.split_merged_cells);
  const notCarriedOver = [];
  if (failedImages > 0) {
    notCarriedOver.push(
      `${failedImages} image(s) that couldn't be imported (replaced with placeholders)`
    );
  }
  if (toCount(skipped.comments) > 0) {
    notCarriedOver.push("comments");
  }
  if (toCount(skipped.footnotes) + toCount(skipped.endnotes) > 0) {
    notCarriedOver.push("footnotes");
  }
  if (toCount(skipped.text_boxes) > 0) {
    notCarriedOver.push("text boxes");
  }
  if (toCount(skipped.charts) + toCount(skipped.smartart) > 0) {
    notCarriedOver.push("charts and drawings");
  }
  if (mergedCells > 0) {
    notCarriedOver.push(`${mergedCells} merged table cell(s) split`);
  }
  const imported = `Imported ${toCount(summary.block_count)} blocks and ${toCount(summary.image_count)} images.`;
  if (!notCarriedOver.length) {
    return imported;
  }
  return `${imported} Not carried over: ${notCarriedOver.join(", ")}.`;
}
